/* global ActionCable,App */
import { useState, useEffect, useRef } from "react";
import { CSSTransition } from "react-transition-group";
import PropTypes from "prop-types";
import dayjs from "@shared/dayjs";
import { capitalize } from "lodash";
import api from "@shared/phoneDncStatusApi";
import { DncFlag } from "@shared/Sms";
import FieldHelper from "@shared/v2/FieldHelper";
import { tryCatchHandlr } from "@shared/Utilities";
import {
  ArrowUpRightFromSquareSolidV6,
  CheckSolidV6,
  GripDotsVerticalSolidV6,
  PhoneHangupSolidV6,
  PhoneSolidV6,
  SparklesSolidV6,
} from "@shared/v2/Icomoon";
import AiLeadCallSummary from "@shared/AiAssistant/AiLeadCallSummary";
import Alert from "@shared/v2/Alert";
import Button from "@shared/v2/Button";
import formatPhoneNumber from "@shared/FormatPhoneNumber";
import FieldLabel from "@shared/v2/FieldLabel";
import StatusDropdown from "@shared/StatusDropdown/StatusDropdown";
import { produce } from "immer";
import StagesDropDown from "@shared/StagesDropDown";
import TwoPartyFlag from "@shared/Sms/TwoPartyFlag";
import FilterPill from "@shared/v2/FilterPill";
import FieldError from "@shared/v2/FieldError";
import MentionablePersonTextArea from "@shared/mentions/MentionablePersonTextArea";
import {
  callStatusIconForPhone,
  mobileCallStatusIcon,
} from "@shared/models/Communications/viewPhoneIcons/CallStatusIcon";
import { caseTransformingAxios, responseDataToCamelCase } from "@shared/v2/caseTransformingAxios";
import CallStatusBar from "@shared/CallStatusBar";
import TabBar from "@shared/v2/TabBar";
import Tab from "@shared/v2/Tab";
import Toggle from "@shared/v2/Toggle";
import { outcomeOptions } from "../CallOutcomeAndNotes";
import {
  useCallRecordingSettings,
  useOutboundCallRecordingSetting,
  useFeatureFlags,
} from "../../../reducers/layoutReducer/selectors";
import RecordConfirmationModal from "../../../Dialer/components/RecordConfirmationModal";
import PhoneStatusIcon from "../PhoneStatusIcon";
import usePanelResize from "../usePanelResize";
import IntentDropdown from "../../../PersonDetail/components/IntentDropdown";
import ConferenceTasks from "./ConferenceTasks";
import AICallSummaryStatus from "../AICallSummaryStatus";
import CallListPreview, { getCallList } from "../CallListPreview";
import WaitingForAgentPickup from "./WaitingForAgentPickup";

const nameFormat = (name) => (name?.trim().length > 0 ? name : "No Name");

const NOTES_MENTION_CLASSNAMES = {
  control: "!tw-rounded-[8px] tw-flex-1 tw-h-full tw-min-h-[125px] !tw-max-h-none",
  highlighter: "tw-h-full tw-min-h-[125px] !tw-max-h-none",
  input: "tw-h-full tw-min-h-[125px] !tw-max-h-none",
};

const callCompleteStatus = ["Completed", "Failed", "Busy", "No-answer", "Canceled"];

const CONFERENCE_TAB_LABELS = {
  leadSummary: "Lead Summary",
  tasks: "Tasks",
  appointments: "Appointments",
};

const getNextUndialedParticipant = (callList, currentPersonUuid) => {
  const currentParticipantIndex = (callList || []).findIndex(
    (participant) => participant.personUuid === currentPersonUuid,
  );

  if (currentParticipantIndex === -1) {
    return (callList || []).find((participant) => !participant.callSid) || null;
  }

  return (
    callList.slice(currentParticipantIndex + 1).find((participant) => !participant.callSid) ||
    callList.slice(0, currentParticipantIndex).find((participant) => !participant.callSid) ||
    null
  );
};

const ConferenceFlyoutCallForm = ({
  enableLeadSummary = true,
  dialerSession,
  closeCallModal,
  initialConferenceCall = null,
}) => {
  const { dialerServiceUrl, dialerServiceToken, isRecoveredSession } = dialerSession;
  const featureFlags = useFeatureFlags();
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [conferenceCall, setConferenceCall] = useState(initialConferenceCall);
  const [websocketsConnected, setWebsocketsConnected] = useState(false);
  const [possibleStaleState, setPossibleStaleState] = useState(false);
  const [currentCallTime, setCurrentCallTime] = useState("0:00:00");
  const [callOutcome, setCallOutcome] = useState({ outcome: null, comment: "" });
  const [endSessionLoading, setEndSessionLoading] = useState(false);
  const [endCallLoading, setEndCallLoading] = useState(false);
  const [endCallErrors, setEndCallErrors] = useState([]);
  const [savingCallOutcome, setSavingCallOutcome] = useState(false);
  const [savingOutcomeErrors, setSavingOutcomeErrors] = useState([]);
  const [savingOutcomeSuccess, setSavingOutcomeSuccess] = useState(null);
  const [savingLeadError, setSavingLeadError] = useState(null);
  const [dncStatuses, setDncStatuses] = useState({});
  const [person, setPerson] = useState({});
  const [isPersonLoading, setPersonLoading] = useState(true);
  const [isPersonStatusLoading, setPersonStatusLoading] = useState(false);
  const [previewedPersonUuid, setPreviewedPersonUuid] = useState(null);
  const [undialedParticipant, setUndialedParticipant] = useState();
  const [callList, setCallList] = useState(null);
  const [recorded, setRecorded] = useState(false);
  const [recordToggleDisabled, setRecordToggleDisabled] = useState(false);
  const [isLeadSummaryEnabled, setIsLeadSummaryEnabled] = useState(enableLeadSummary);
  const [selectedTab, setSelectedTab] = useState(
    featureFlags.dialerLeadSummary && enableLeadSummary
      ? CONFERENCE_TAB_LABELS.leadSummary
      : CONFERENCE_TAB_LABELS.tasks,
  );
  const leadSummaryRef = useRef(null);
  const tasksRef = useRef(null);
  const appointmentsRef = useRef(null);
  const { outboundRecording } = useOutboundCallRecordingSetting();
  const callRecordingSettings = useCallRecordingSettings();
  const aiCallSummaryEnabled = callRecordingSettings?.aiCallSummary ?? true;
  const scrollRef = useRef(null);
  const { containerRef, leftWidth, rightWidth, startResize, handleResizeKeyDown } = usePanelResize({
    initialLeftWidth: 550,
    leftMinWidth: 550,
    rightMinWidth: 200,
    rightMaxWidth: 400,
    handleWidth: 8,
    handleSpacing: 9,
  });
  const conferenceCallRef = useRef(conferenceCall);
  const conferenceStartedRef = useRef(isRecoveredSession);
  const personRequestAbortControllerRef = useRef(null);
  conferenceCallRef.current = conferenceCall;

  const currentParticipant = conferenceCall?.currentParticipant;
  const agentParticipant = conferenceCall?.agentParticipant;

  const currentPhoneNumber = currentParticipant?.phoneNumber;
  const currentParticipantCallSid = currentParticipant?.callSid;
  const currentParticipantUuid = currentParticipant?.personUuid;
  const currentCallStatus = currentParticipant?.humanizedCallStatus;
  const agentCallStatus = agentParticipant?.humanizedCallStatus;

  const hasAgentConnectedRef = useRef(agentCallStatus === "In-progress");
  if (agentCallStatus === "In-progress") hasAgentConnectedRef.current = true;
  else if (callCompleteStatus.includes(agentCallStatus)) hasAgentConnectedRef.current = false;
  const isAgentConnected = hasAgentConnectedRef.current;

  const callComplete = callCompleteStatus.includes(currentCallStatus);
  // Notes are enabled once the participant is dialed, until the outcome is saved
  const notesDisabled = !currentCallStatus || Boolean(savingOutcomeSuccess);

  const nextParticipant =
    undialedParticipant?.personUuid && undialedParticipant.personUuid !== currentParticipantUuid
      ? undialedParticipant
      : null;
  const nextPhoneNumber = nextParticipant?.phoneNumber;

  const currentParticipantPosition = conferenceCall?.currentParticipantPosition ?? 0;
  const totalParticipants = conferenceCall?.totalParticipants ?? 0;

  const total = callList ? callList.length : totalParticipants;
  const totalCallListSids = callList
    ? callList.filter(
        (participant) =>
          Boolean(participant.callSid) ||
          Boolean(currentParticipantCallSid && participant.personUuid === currentParticipantUuid),
      ).length
    : 0;
  let current = currentParticipantPosition;
  if (callList) {
    const outcomeSaved = Boolean(savingOutcomeSuccess);
    const activeCount = outcomeSaved ? totalCallListSids + 1 : Math.max(totalCallListSids, 1);
    current = total > 0 ? Math.min(activeCount, total) : 0;
  }

  const primaryPhoneNumber = person?.data?.attributes?.primaryPhoneNumber;

  const phoneDetails =
    person?.included?.find(
      (item) =>
        item.type === "phone_detail" && item.id !== null && item.attributes?.value === primaryPhoneNumber,
    )?.attributes || null;

  const callUnsubscribed =
    phoneDetails?.callUnsubscribed ||
    phoneDetails?.callSoftUnsubscribed ||
    phoneDetails?.callAccountUnsubscribed;

  const isSmsTextable = phoneDetails?.smsTextable;

  let callStatus;
  if (callUnsubscribed) {
    callStatus = "unsubscribed";
  } else if (!isSmsTextable) {
    callStatus = "voiceOnly";
  } else {
    callStatus = "active";
  }

  const callIcon =
    phoneDetails?.category === "mobile"
      ? mobileCallStatusIcon(callStatus)
      : callStatusIconForPhone(callStatus);

  const getPhoneStatuses = (cList) => {
    api.index(cList.map((participant) => participant.phoneNumber)).then((res) => {
      setDncStatuses((oldState) => ({ ...oldState, ...res.data }));
    });
  };

  const toggleRecording = (recordingConsent) => {
    setRecordToggleDisabled(true);

    caseTransformingAxios
      .post(`${dialerServiceUrl}/dialer/${!recorded ? "start" : "stop"}_recording`, {
        token: dialerServiceToken,
        ...(!recorded
          ? { recording_consent: featureFlags.callRecordingAdjustments || recordingConsent || false }
          : {}),
      })
      .then(() => {
        setRecordToggleDisabled(false);
        setRecorded(!recorded);
      })
      .catch((err) => {
        if (err.response?.data?.error === "Recording consent is required") {
          setShowConsentModal(true);
        } else {
          setRecordToggleDisabled(false);
        }
      });
  };

  const getPerson = async (uuid) => {
    personRequestAbortControllerRef.current?.abort();
    personRequestAbortControllerRef.current = null;
    setPerson({});

    if (!uuid) {
      setPersonLoading(false);
      return;
    }

    const abortController = new AbortController();
    personRequestAbortControllerRef.current = abortController;

    setPersonLoading(true);
    const [res, err] = await tryCatchHandlr(
      caseTransformingAxios.get(`/api/v4/person/${uuid}/dialer_details`, { signal: abortController.signal }),
    );

    if (personRequestAbortControllerRef.current !== abortController) return;

    personRequestAbortControllerRef.current = null;
    setPersonLoading(false);

    if (err) {
      return;
    }

    setPerson({
      ...res.data.person,
      uuid: res.data.uuid,
      leadId: res.data.leadId,
    });
  };

  useEffect(
    () => () => {
      personRequestAbortControllerRef.current?.abort();
    },
    [],
  );
  useEffect(() => {
    setPersonStatusLoading(false);
    getPerson(currentParticipantUuid);
  }, [currentParticipantUuid]);

  const startConference = () => {
    caseTransformingAxios
      .post(`${dialerServiceUrl}/dialer/start_dialer_session`, {
        token: dialerServiceToken,
      })
      .then((resp) => {
        const participant = resp.data.currentParticipant;
        setConferenceCall((currentConferenceCall) => ({
          ...currentConferenceCall,
          ...resp.data,
          currentParticipant: participant,
        }));
      });
  };

  const fetchCurrentConferenceState = () => {
    caseTransformingAxios
      .get(`${dialerServiceUrl}/dialer/fetch_dialer_state?token=${dialerServiceToken}`)
      .then((resp) => {
        setConferenceCall(resp.data);
      });
  };

  const setupSubscription = () => {
    if (typeof App !== "undefined") {
      const websocketConnected = () => {
        setWebsocketsConnected(true);
        if (possibleStaleState) {
          fetchCurrentConferenceState();
        }
      };

      const websocketDisconnected = () => {
        setWebsocketsConnected(false);
        setPossibleStaleState(true);
      };

      App.dialer_cable = ActionCable.createConsumer(`${dialerServiceUrl}/cable?token=${dialerServiceToken}`);
      App.phone_call = App.dialer_cable.subscriptions.create("ConferenceChannel", {
        connected: function connected() {
          // eslint-disable-next-line react/no-this-in-sfc
          this.followCurrentCall();
          websocketConnected();
        },
        received: function received(data) {
          setConferenceCall(responseDataToCamelCase(data.conference));
        },
        disconnected: function disconnected() {
          websocketDisconnected();
        },
        rejected: function rejected() {
          websocketDisconnected();
        },
        failed: function failed() {
          websocketDisconnected();
        },
        followCurrentCall: function followCurrentCall() {
          // eslint-disable-next-line react/no-this-in-sfc
          this.perform("follow");
        },
      });
    }
  };

  useEffect(() => {
    const currentStatus = currentParticipant?.humanizedCallStatus;
    if (outboundRecording === "automatic" && currentStatus === "In-progress") {
      toggleRecording();
    }
  }, [outboundRecording, currentParticipant?.humanizedCallStatus]);

  useEffect(() => {
    let timer = null;

    const updateCallTime = () => {
      const activeParticipant = conferenceCallRef?.current?.currentParticipant;
      const status = activeParticipant?.humanizedCallStatus;
      const answeredTimestamp = activeParticipant?.callAnsweredTimestamp;

      if (status === "In-progress" && answeredTimestamp) {
        const callTime = dayjs
          .utc(dayjs.duration(dayjs().diff(dayjs(answeredTimestamp))).asMilliseconds())
          .format("H:mm:ss");
        setCurrentCallTime(callTime);
      }
    };

    const manageSubscriptionAndTimer = () => {
      if (typeof App !== "undefined" && App.phone_call) {
        App.phone_call.unsubscribe();
      }
      clearInterval(timer);
    };

    if (isRecoveredSession || conferenceStartedRef.current) {
      fetchCurrentConferenceState();
    } else {
      conferenceStartedRef.current = true;
      startConference();
    }
    setupSubscription();
    timer = setInterval(updateCallTime, 1000);

    return () => {
      manageSubscriptionAndTimer();
    };
  }, []);

  useEffect(() => {
    if (dialerServiceUrl && dialerServiceToken) {
      getCallList(dialerServiceUrl, dialerServiceToken).then((list) => {
        setCallList(list);
        getPhoneStatuses(list);
      });
    }
  }, [dialerServiceUrl, dialerServiceToken, currentParticipantCallSid]);

  useEffect(() => {
    if (callList) {
      const firstUndialed = getNextUndialedParticipant(callList, currentParticipantUuid);
      setUndialedParticipant(firstUndialed);
    }
  }, [callList, currentParticipantUuid]);

  const isDnc = () => api.isDnc(currentPhoneNumber, dncStatuses);
  const isNextDnc = () => api.isDnc(nextPhoneNumber, dncStatuses);

  if (typeof App !== "undefined" && App?.leadUpdateChannel) {
    App.leadUpdateChannel.received = (data) => {
      if (data.status === "success" && String(data.lead?.id) === String(person.leadId)) {
        getPerson(currentParticipantUuid);
        setSavingLeadError(null);
      }
      if (
        isPersonStatusLoading &&
        (data.status === "failed" || String(data.lead?.id) === String(person.leadId))
      ) {
        if (data.status === "failed") {
          setSavingLeadError(data.message);
        }
        setPersonStatusLoading(false);
      }
      if (data.status === "failed") setSavingLeadError(data.message);
    };
  }
  const setStatus = async (newStatus) => {
    setPersonStatusLoading(true);
    setSavingLeadError(null);

    window.App.leadUpdateChannel.perform("lead_update", {
      id: person.leadId,
      person_attributes: {
        status: newStatus,
      },
    });
  };

  const updateIntent = async (key, value) => {
    if (!person?.data?.id || person?.data?.attributes?.[key] === value) return;

    const previousValue = person.data.attributes[key];
    setSavingLeadError(null);

    setPerson((oldPerson) =>
      produce(oldPerson, (draft) => {
        if (!draft?.data?.attributes) return;
        // eslint-disable-next-line no-param-reassign
        draft.data.attributes[key] = value;
      }),
    );

    try {
      await caseTransformingAxios.patch(`/api/v4/contact_people/${person.data.id}`, {
        person: {
          [key]: value,
          id: person.data.id,
        },
      });
    } catch (err) {
      setPerson((oldPerson) =>
        produce(oldPerson, (draft) => {
          if (!draft?.data?.attributes) return;
          // eslint-disable-next-line no-param-reassign
          draft.data.attributes[key] = previousValue;
        }),
      );
      setSavingLeadError(
        err.response?.data?.errors?.join(", ") || "An error occurred while updating intent.",
      );
    }
  };

  const hangUpCall = () => {
    setEndCallLoading(true);
    caseTransformingAxios
      .post(`${dialerServiceUrl}/dialer/end_current_call`, {
        token: dialerServiceToken,
      })
      .then((resp) => {
        setConferenceCall(resp.data);
        setEndCallLoading(false);
        setEndCallErrors([]);
      })
      .catch(() => {
        setEndCallLoading(false);
        setEndCallErrors(["There was an error ending the current call, please try again."]);
      });
  };

  const dialNextLead = (personUuid = previewedPersonUuid || currentParticipantUuid) => {
    setRecorded(false);

    caseTransformingAxios
      .post(`${dialerServiceUrl}/dialer/dial_lead`, {
        token: dialerServiceToken,
        personUuid,
      })
      .then((resp) => {
        setConferenceCall(resp.data);
        setSavingOutcomeSuccess(null);
      });
  };

  const handleOutcomeInputChange = (key, value) => {
    setSavingOutcomeErrors([]);
    setCallOutcome({ ...callOutcome, [key]: value });
  };

  const endDialerSession = () => {
    setEndSessionLoading(true);
    let callOutcomePromise = Promise.resolve();
    // eslint-disable-next-line no-use-before-define
    if (callOutcome.outcome) callOutcomePromise = submitOutcome(true);
    callOutcomePromise.finally(() => {
      caseTransformingAxios
        .post(`${dialerServiceUrl}/dialer/end_dialer_session`, {
          token: dialerServiceToken,
        })
        .then((resp) => {
          setConferenceCall(resp.data);
          setEndSessionLoading(false);
          closeCallModal();
        })
        .catch(() => {
          setEndSessionLoading(false);
          closeCallModal();
        })
        .then(() => {
          window.dispatchEvent(new CustomEvent("lead-index:reload"));
        });
    });
  };

  const onPreview = (personUuid) =>
    Promise.allSettled([
      caseTransformingAxios.get(`${dialerServiceUrl}/dialer/preview_lead`, {
        params: {
          token: dialerServiceToken,
          personUuid,
        },
      }),
    ]).then((resp) => {
      const previewedPerson = resp[0].value?.data;
      const previewedUuid = previewedPerson?.personUuid;
      setPreviewedPersonUuid(previewedUuid);
      setConferenceCall((prev) => ({
        ...prev,
        currentParticipant: previewedPerson,
      }));
      return true;
    });

  const submitOutcome = async (pause = false) => {
    if (!callOutcome.outcome) {
      setSavingOutcomeErrors(["Please select an outcome before saving."]);
      return false;
    }
    setSavingCallOutcome(true);

    return caseTransformingAxios
      .post(`${dialerServiceUrl}/dialer/save_outcome`, {
        token: dialerServiceToken,
        participantId: currentParticipant?.id,
        ...callOutcome,
      })
      .then((resp) => {
        setCurrentCallTime(null);
        setCallOutcome({ outcome: null, comment: "" });
        setSavingOutcomeErrors([]);
        setSavingCallOutcome(false);
        setSavingOutcomeSuccess(true);
        if (pause) {
          setConferenceCall(resp.data);
          return true;
        }

        return getCallList(dialerServiceUrl, dialerServiceToken).then((updatedCallList) => {
          setCallList(updatedCallList);
          const nextUndialedParticipant = getNextUndialedParticipant(updatedCallList, currentParticipantUuid);

          if (nextUndialedParticipant) {
            dialNextLead(nextUndialedParticipant.personUuid);
          } else {
            setConferenceCall(resp.data);
          }

          return true;
        });
      })
      .catch(() => {
        setSavingCallOutcome(false);
        setSavingOutcomeErrors(["There was an error saving the call outcome. Please try again."]);
        return false;
      });
  };

  const saveAndPreview = (personUuid) => {
    if (!callOutcome.outcome) {
      setSavingOutcomeErrors(["Please select an outcome before saving."]);
      return Promise.resolve(false);
    }

    return submitOutcome(true).then((wasSaved) => {
      if (!wasSaved) return false;
      return onPreview(personUuid);
    });
  };

  const conferenceStatus = conferenceCall?.conferenceStatus;

  const websocketError = conferenceCall && conferenceStatus !== "conference-end" && !websocketsConnected;

  const renderWebsocketError = () => (
    <div className="alert alert-danger">
      <div className="text-center">
        <i className="fa fa-exclamation-triangle tw-text-30px" />
        <br />
        <span className="tw-text-20px tw-font-bold">Connection Unstable</span>
        <br />
        <span>Your browser has lost connection with Brivity Dialer.</span>
        <br />
        <span>Please wait while we attempt to reconnect...</span>
        <br />
        <i className="fa fa-spinner fa-pulse" />
      </div>
    </div>
  );

  const renderLeadError = () => (
    <div className="alert alert-danger">
      <div className="text-center">
        <span>{savingLeadError}</span>
      </div>
    </div>
  );

  const conferenceCallIsComplete =
    conferenceCall && conferenceStatus === "conference-end" && savingOutcomeSuccess;

  const saveContinueBtnDisabled = !callComplete || savingCallOutcome;

  const hasNextParticipant = Boolean(nextParticipant);

  const saveBtnText = (() => {
    if (!conferenceCall && conferenceCallIsComplete) return "Save + Complete";
    if (hasNextParticipant) return "Save + Dial Next";
    return "Save + Complete";
  })();

  const conferenceIsReady =
    websocketsConnected && conferenceCall && currentParticipant && !conferenceCallIsComplete;

  const getTabIndex = (tabLabel) => {
    const tabMap = {
      [CONFERENCE_TAB_LABELS.leadSummary]: 0,
      [CONFERENCE_TAB_LABELS.tasks]: 1,
      [CONFERENCE_TAB_LABELS.appointments]: 2,
    };
    return tabMap[tabLabel] ?? 1;
  };

  const handleTabChange = (tabLabel) => {
    setSelectedTab(tabLabel);
  };

  const handleToggleLeadSummary = (e) => {
    const next = typeof e?.target?.checked === "boolean" ? e.target.checked : !isLeadSummaryEnabled;
    setIsLeadSummaryEnabled(next);
    if (next) {
      setSelectedTab(CONFERENCE_TAB_LABELS.leadSummary);
    } else {
      setSelectedTab(CONFERENCE_TAB_LABELS.tasks);
    }
  };

  // Generate inline styles based on transition state, current tab being animated, and selected tab
  const getTransitionStyle = (state, tabLabel) => {
    const currentTabIndex = getTabIndex(tabLabel);
    const selectedTabIndex = getTabIndex(selectedTab);
    const isMovingForward = currentTabIndex < selectedTabIndex;
    const isEntering = state === "entering" || state === "entered";

    const exitTransform = isMovingForward ? "-100%" : "100%";

    return {
      opacity: isEntering ? 1 : 0,
      transform: isEntering ? "translateX(0)" : `translateX(${exitTransform})`,
      pointerEvents: isEntering ? "auto" : "none",
      zIndex: isEntering ? 1 : 0,
      transition: "opacity 300ms ease-in-out, transform 300ms cubic-bezier(0.4, 0, 0.2, 1)",
    };
  };

  const isWaitingForAgentPickup =
    conferenceStatus !== "conference-end" && !currentCallStatus && !isAgentConnected;

  if (isWaitingForAgentPickup) {
    return (
      <>
        <div className="tw-flex-1 tw-flex tw-flex-col">
          <WaitingForAgentPickup
            agentPhoneNumber={agentParticipant?.phoneNumber}
            onEndSession={endDialerSession}
            endSessionLoading={endSessionLoading}
          />
        </div>

        {websocketError && renderWebsocketError()}
      </>
    );
  }

  return (
    <>
      <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-[24px]">
        <div className="tw-flex tw-justify-between">
          <div className="tw-flex tw-items-center tw-gap-[16px]">
            <a
              href={currentParticipant?.personUrl}
              onClick={() => {
                window.clarity?.("event", "conferenceDialerPDPLink");
              }}
              target="_blank"
              rel="noreferrer"
              className="tw-text-18d tw-font-bold tw-inline-flex tw-items-center tw-gap-[6px]"
            >
              {nameFormat(currentParticipant?.personFullName)}
              <ArrowUpRightFromSquareSolidV6 className="tw-text-theme-primary" size="l" />
            </a>
            <div className="tw-text-14d tw-font-semibold tw-text-gray-50 overflow tw-flex tw-items-center tw-gap-[4px]">
              <span>
                {phoneDetails?.category ? `${capitalize(phoneDetails.category)}:` : ""} {currentPhoneNumber}
              </span>
              <DncFlag show={isDnc()} />
              <TwoPartyFlag show={currentParticipant?.recordingConsentRequired} />
              {person?.included && (
                <PhoneStatusIcon callIcon={callIcon.icon({ size: "m" })} tooltip={callIcon.tooltip} />
              )}
            </div>
          </div>
          <div className="tw-max-w-[208px] tw-w-full">
            <div className="tw-text-12d tw-text-gray-50 tw-mb-5px">
              Call {current} of {total}
            </div>
            <div className="tw-w-full tw-h-8px tw-rounded-[16px] tw-bg-[#000000] tw-bg-opacity-20 tw-overflow-hidden">
              <div
                role="progressbar"
                aria-label="Call Progress"
                className="progress-bar call-status tw-bg-theme-primary-alternative"
                aria-valuenow={current}
                aria-valuemin="0"
                aria-valuemax={total || 100}
                style={{
                  width: `${total > 0 ? (((current || 0) / total) * 100).toFixed() : 0}%`,
                }}
              />
            </div>
          </div>
        </div>

        {((callOutcome.outcome && savingOutcomeErrors.length > 0) || endCallErrors.length > 0) && (
          <div className="tw-flex tw-flex-col tw-gap-[8px]">
            {[...savingOutcomeErrors, ...endCallErrors].map((errorMessage) => (
              <Alert key={errorMessage} variant="error" text={errorMessage} />
            ))}
          </div>
        )}

        <div className="tw-px-[2px] tw-flex tw-gap-[30px]">
          <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-[8px] tw-max-w-[164px]">
            <FieldLabel label="Status" />
            <StatusDropdown
              person={{}}
              status={person?.data?.attributes?.status}
              statusOptions={person?.meta?.statusOptions}
              btnClassName="tw-m-0 tw-w-full"
              onChange={setStatus}
              isLoading={isPersonLoading || isPersonStatusLoading}
            />
          </div>
          <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-[8px] tw-max-w-[164px]">
            <FieldLabel label="Stage" />
            <StagesDropDown
              selectedStage={person?.data?.attributes?.stage}
              isLoading={isPersonLoading}
              onUpdateStage={(stage) =>
                window.App.leadUpdateChannel.perform("lead_update", {
                  id: person.leadId,
                  person_attributes: {
                    stage: stage.value,
                    stage_type: stage.type,
                  },
                })
              }
              containerClassName="!tw-border-[1.5px] !tw-border-solid !tw-border-gray-50 hover:!tw-border-gray-50 tw-w-full tw-justify-center"
            />
          </div>
          <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-[8px] tw-max-w-[120px]">
            <FieldLabel label="Intent" />
            <IntentDropdown person={person} onUpdate={updateIntent} />
          </div>
          <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-[8px] tw-overflow-hidden tw-whitespace-nowrap">
            <FieldLabel label="Source" />
            <span
              className="tw-overflow-hidden tw-text-ellipsis"
              title={person?.data?.attributes?.source || "No Source"}
            >
              {person?.data?.attributes?.source || "No Source"}
            </span>
          </div>
        </div>

        <div className="tw-flex-1 tw-flex tw-flex-col tw-overflow-y-auto tw-min-h-0" ref={scrollRef}>
          <div className="tw-flex-1 tw-flex tw-min-w-0 tw-min-h-0 tw-items-stretch" ref={containerRef}>
            <div
              className="tw-pb-[4px] tw-shrink-0 tw-min-w-0 tw-flex tw-flex-col tw-min-h-0 tw-rounded-[8px] tw-border tw-border-solid tw-border-gray-15"
              style={{ width: `${leftWidth}px` }}
            >
              <TabBar containerClassName="tw-mx-[16px]" className="tw-w-full">
                {featureFlags.dialerLeadSummary && isLeadSummaryEnabled && (
                  <Tab
                    label={
                      <span className="tw-flex tw-items-center tw-gap-[4px]">
                        {CONFERENCE_TAB_LABELS.leadSummary}{" "}
                        <SparklesSolidV6 className="tw-text-semantic-purple-100" />
                      </span>
                    }
                    selected={selectedTab === CONFERENCE_TAB_LABELS.leadSummary}
                    onClick={() => handleTabChange(CONFERENCE_TAB_LABELS.leadSummary)}
                  />
                )}
                <Tab
                  label={CONFERENCE_TAB_LABELS.tasks}
                  selected={selectedTab === CONFERENCE_TAB_LABELS.tasks}
                  onClick={() => handleTabChange(CONFERENCE_TAB_LABELS.tasks)}
                />
                <Tab
                  label={CONFERENCE_TAB_LABELS.appointments}
                  selected={selectedTab === CONFERENCE_TAB_LABELS.appointments}
                  onClick={() => handleTabChange(CONFERENCE_TAB_LABELS.appointments)}
                />
                {featureFlags.dialerLeadSummary && (
                  <Toggle
                    checked={isLeadSummaryEnabled}
                    onChange={handleToggleLeadSummary}
                    trackClassName={isLeadSummaryEnabled ? "!tw-bg-semantic-purple-100" : ""}
                    text={
                      <div className="tw-bg-wildberry-70 tw-bg-clip-text tw-text-transparent">
                        Gabbi Call Assist
                      </div>
                    }
                    labelClassName="tw-mb-0 tw-ml-auto tw-self-center"
                    labelPlacement="start"
                  />
                )}
              </TabBar>

              <div className="tw-relative tw-flex-1 tw-min-h-[120px] tw-overflow-hidden">
                <CSSTransition
                  in={selectedTab === CONFERENCE_TAB_LABELS.leadSummary}
                  timeout={300}
                  nodeRef={leadSummaryRef}
                  unmountOnExit={false}
                >
                  {(state) => (
                    <div
                      ref={leadSummaryRef}
                      style={getTransitionStyle(state, CONFERENCE_TAB_LABELS.leadSummary)}
                      className="tw-absolute tw-inset-0 tw-flex tw-bg-white tw-overflow-y-auto tw-p-[16px]"
                    >
                      {featureFlags.dialerLeadSummary && (
                        <AiLeadCallSummary leadPersonUuid={currentParticipantUuid} />
                      )}
                    </div>
                  )}
                </CSSTransition>
                <CSSTransition
                  in={selectedTab === CONFERENCE_TAB_LABELS.tasks}
                  timeout={300}
                  nodeRef={tasksRef}
                  unmountOnExit={false}
                >
                  {(state) => (
                    <div
                      ref={tasksRef}
                      style={getTransitionStyle(state, CONFERENCE_TAB_LABELS.tasks)}
                      className="tw-absolute tw-inset-0 tw-flex tw-bg-white tw-text-14d tw-font-semibold tw-text-gray-75 tw-overflow-y-auto"
                    >
                      <ConferenceTasks personUuid={currentParticipantUuid} />
                    </div>
                  )}
                </CSSTransition>
                <CSSTransition
                  in={selectedTab === CONFERENCE_TAB_LABELS.appointments}
                  timeout={300}
                  nodeRef={appointmentsRef}
                  unmountOnExit={false}
                >
                  {(state) => (
                    <div
                      ref={appointmentsRef}
                      style={getTransitionStyle(state, CONFERENCE_TAB_LABELS.appointments)}
                      className="tw-absolute tw-inset-0 tw-flex tw-bg-white tw-text-14d tw-font-semibold tw-text-gray-75 tw-overflow-y-auto"
                    >
                      <ConferenceTasks personUuid={currentParticipantUuid} type="Appointment" />
                    </div>
                  )}
                </CSSTransition>
              </div>
            </div>
            <button
              type="button"
              aria-label="resize notes"
              className="tw-shrink-0 tw-w-[8px] tw-ml-[5px] tw-mr-[4px] tw-p-0 tw-border-0 tw-bg-transparent tw-cursor-col-resize tw-flex tw-justify-center tw-items-center tw-group"
              onMouseDown={startResize}
              onKeyDown={handleResizeKeyDown}
            >
              <GripDotsVerticalSolidV6 />
            </button>
            <div
              className="tw-p-[1px] tw-shrink-0 tw-min-w-0 tw-flex tw-flex-col tw-min-h-0"
              style={{
                width: `${rightWidth}px`,
              }}
            >
              <div className="tw-relative tw-flex-1 tw-flex tw-flex-col">
                <MentionablePersonTextArea
                  id="interaction-phone-input"
                  className="tw-flex-1 tw-h-full tw-min-h-0 tw-border-solid tw-border-gray-15 tw-border-[1px] tw-rounded-[8px]"
                  classNames={NOTES_MENTION_CLASSNAMES}
                  value={callOutcome.comment || ""}
                  isUuid
                  placeholder={notesDisabled ? "Take some notes when this call starts" : null}
                  mentionable_id={currentParticipantUuid}
                  mentionable_type="Person"
                  onChange={(value) => handleOutcomeInputChange("comment", value)}
                  isExternal={false}
                  disabled={notesDisabled}
                />
              </div>
              {currentParticipant && recorded && aiCallSummaryEnabled && (
                <AICallSummaryStatus callComplete={callComplete} />
              )}
              {currentParticipant && (
                <FieldHelper className="tw-mt-[4px]" helperText="Notes will be saved with this call." />
              )}
            </div>
          </div>
        </div>

        <hr className="tw-border-gray-15 tw-border-t tw-mx-0 tw-my-[8px]" />

        {!conferenceCallIsComplete && (
          <div className="tw-flex tw-flex-col tw-gap-[16px]">
            <div className="tw-flex tw-items-center tw-gap-[20px]">
              <div className="tw-flex-1">
                <CallStatusBar
                  agentCallStatus={isAgentConnected ? "In-progress" : agentCallStatus}
                  callStatus={currentCallStatus}
                  currentCallTime={currentCallTime}
                  isCompleted={callComplete}
                  recorded={recorded}
                  recordToggleDisabled={recordToggleDisabled || outboundRecording === "disabled"}
                  onToggleChange={() => toggleRecording()}
                  agentPhoneNumber={agentParticipant?.phoneNumber}
                />
              </div>
              <div className="tw-flex tw-gap-[8px]">
                {currentCallStatus && (
                  <Button
                    disabled={callComplete}
                    isLoading={endCallLoading}
                    onClick={hangUpCall}
                    size="medium"
                    schema="solid-red"
                    className="tw-min-w-[180px] tw-whitespace-nowrap tw-inline-flex tw-justify-center tw-items-center tw-gap-[4px]"
                  >
                    {callComplete ? (
                      <>
                        <CheckSolidV6 /> Call Ended
                      </>
                    ) : (
                      <>
                        <PhoneHangupSolidV6 size="m" /> End Call
                      </>
                    )}
                  </Button>
                )}

                {!currentCallStatus && (
                  <Button
                    disabled={!isAgentConnected}
                    onClick={() => dialNextLead()}
                    size="medium"
                    schema="solid-green"
                    className="tw-min-w-[180px] tw-whitespace-nowrap tw-inline-flex tw-justify-center tw-items-center tw-gap-[4px]"
                  >
                    <PhoneSolidV6 size="m" />
                    Dial
                  </Button>
                )}

                {callComplete && (
                  <Button
                    className="tw-whitespace-nowrap tw-inline-flex tw-items-center tw-gap-[4px]"
                    disabled={saveContinueBtnDisabled}
                    onClick={() => submitOutcome()}
                    isLoading={savingCallOutcome}
                    size="medium"
                    schema={hasNextParticipant ? "solid-green" : undefined}
                  >
                    {hasNextParticipant && <PhoneSolidV6 size="m" />}
                    {saveBtnText}
                  </Button>
                )}
              </div>
            </div>

            <div
              className={`tw-flex tw-self-end tw-justify-end tw-items-start tw-gap-[8px] ${!callOutcome.outcome && savingOutcomeErrors.length > 0 ? "tw-border-solid tw-border-[1px] tw-border-semantic-red-120 tw-rounded-[8px] tw-pb-[8px] tw-pt-[12px] tw-px-[8px]" : ""}`}
            >
              {!callOutcome.outcome && savingOutcomeErrors.length > 0 && (
                <FieldError className="!tw-text-14d" error={savingOutcomeErrors[0]} />
              )}
              <FieldLabel className="tw-whitespace-nowrap" label="Call Outcome" isRequired />
              {outcomeOptions.map((k) => (
                <FilterPill
                  key={k.value}
                  variant="primary"
                  selected={callOutcome.outcome === k.value}
                  onClick={() => handleOutcomeInputChange("outcome", k.value)}
                  className={`tw-border-[1px] tw-border-solid tw-border-gray-15 tw-text-gray-75 tw-h-[36px] tw-py-[8px] tw-px-[12px] ${
                    callOutcome.outcome === k.value
                      ? "tw-text-white tw-bg-theme-button-primary"
                      : "tw-bg-transparent"
                  }`}
                >
                  {k.label}
                </FilterPill>
              ))}
            </div>
          </div>
        )}

        <div className="tw-flex tw-items-center tw-justify-between tw-gap-[8px]">
          <Button
            className="tw-whitespace-nowrap"
            schema="tertiary"
            onClick={endDialerSession}
            isLoading={endSessionLoading}
            size="medium"
          >
            End Session
          </Button>

          {conferenceCallIsComplete && (
            <div className="tw-w-full alert alert-info !tw-m-0 !tw-p-[8px] tw-text-18d tw-font-bold tw-text-center">
              Dialer Session Complete
            </div>
          )}

          {!conferenceCallIsComplete && conferenceIsReady && (
            <CallListPreview
              dialer={{ dialerServiceUrl, dialerServiceToken }}
              callList={callList}
              participant={
                nextParticipant
                  ? {
                      dnc: isNextDnc(),
                      twoParty: nextParticipant?.recordingConsentRequired,
                      pdpLink: `/people/${nextParticipant?.personUuid}`,
                      phoneNumber: formatPhoneNumber(nextPhoneNumber),
                      name: nameFormat(nextParticipant?.personFullName),
                      callIcon: callIcon.icon({ size: "m" }),
                      tooltip: callIcon.tooltip,
                      isDataFetched: Boolean(person?.uuid),
                    }
                  : null
              }
              previewState={{
                isCallInProgress: Boolean(currentCallStatus) && !callComplete,
                shouldSaveBeforePreview: callComplete,
                isDisabled: !hasNextParticipant,
                refreshKey: currentParticipantCallSid,
                currentParticipantUuid,
              }}
              actions={{
                onPreview,
                onSaveAndPreview: saveAndPreview,
                onNextParticipantChange: (p) => {
                  if (p && p.personUuid === currentParticipantUuid) {
                    const next = getNextUndialedParticipant(callList, currentParticipantUuid);
                    setUndialedParticipant(next);
                  } else {
                    setUndialedParticipant(p);
                  }
                },
              }}
            />
          )}
        </div>
      </div>

      {websocketError && renderWebsocketError()}
      {savingLeadError && renderLeadError()}

      <RecordConfirmationModal
        show={showConsentModal}
        closeModal={() => {
          setRecordToggleDisabled(false);
          setShowConsentModal(false);
        }}
        onClickRecord={() => {
          toggleRecording(true);
          setShowConsentModal(false);
        }}
      />
    </>
  );
};

ConferenceFlyoutCallForm.propTypes = {
  dialerSession: PropTypes.shape({
    dialerServiceUrl: PropTypes.string.isRequired,
    dialerServiceToken: PropTypes.string.isRequired,
    isRecoveredSession: PropTypes.bool.isRequired,
  }).isRequired,
  closeCallModal: PropTypes.func.isRequired,
  enableLeadSummary: PropTypes.bool,
  initialConferenceCall: PropTypes.shape({}),
};

export default ConferenceFlyoutCallForm;
